import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { FormControl, FormHelperText, Grid, IconButton, Stack, Alert, Tooltip } from '@vapor/material'
import { Divider } from '@mui/material'
import { DatePicker, DateValidationError, PickerChangeHandlerContext } from '@mui/x-date-pickers-pro'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { CityTaxExonerationCode, CustomerDetailFragment, ReservedResourcePhase, useCustomerDocumentImagesQuery } from 'src/api/api.generated'
import GuestTypeSelect from './components/guest-type-select'
import GenderSelect from './components/gender-select'
import CountryMunicipalityLookup from 'src/app/components/country-municipality-lookup/country-municpality-lookup'
import { GuestAddress, GuestCheckinCheckoutAction, GuestOccupancy, MenuActionType } from '../../../../types/guests.types'
import { GuestFormError } from '../form/types'
import useGetErrorMessage from 'src/app/hooks/error-message/error-message'
import { Theme } from '@mui/system'
import { useTranslation } from 'react-i18next'
import { Context, RRContext } from '../../../../context/reservation-context.types'
import { useConfirm } from 'hic-components'
import OccupancyGuestLookup from './components/occupancy-guest-lookup'
import { compareDates, isValidDate } from 'src/utils/date'
import { CountryMunicipality } from 'src/app/components/country-municipality-lookup/country-municpality-lookup.types'
import CityTaxExonerationLookup from 'src/app/components/city-tax-exoneration-lookup/city-tax-exoneration-lookup'
import EditButton from './components/edit-button'
import CountrySelect from './components/country-select'
import IdentificationDocument from './components/identification-document'
import { isGuarantor } from '../../../../utils/guest-occupancy'
import { ReservedResourcePhaseCell } from '../../commons/grid-cells'
import { GuestOccupancyCheckInWarning } from '../../check-in/check.in.types'
import { getGuestCheckInWarningMessage } from '../../check-in/check-in-utils'
import { HicFormLabel } from 'src/app/components/form-fields/hic-form-label.styled'
import { useHotelDate, useTenant } from 'src/app/hooks/context/context.hook'
import { FrontendPhase, useFrontendPhase } from '../../../../utils/phase'
import { faTrash } from '@fortawesome/pro-regular-svg-icons'
import { isGroupMemberToConsiderAsSingleGuest } from '../../../../utils/guests.utils'
import { guestReductionOperationType } from '../../../../api/reserved-resource-utils'
import useFormatDate from 'src/app/hooks/current-locale/use-format-date'

export type GuestFormProps = {
  propertyId: string
  index: number
  context: Context
  rrContext: RRContext
  value: GuestOccupancy
  onChange: (value: GuestOccupancy) => void
  onCustomerUpserted: (customer: CustomerDetailFragment) => void
  onReduceGuestCount: () => Promise<void>
  customerErrorShown: () => void
  checkInWarning?: GuestOccupancyCheckInWarning
  formErrors?: GuestFormError
  disabled?: boolean
  performGuestCheckinCheckout: (guestId: string, action: GuestCheckinCheckoutAction) => void
  cityTaxExonerationFromReservation?: CityTaxExonerationCode | null
}

export default function GuestForm({
  propertyId,
  index,
  context,
  rrContext,
  value,
  checkInWarning,
  formErrors,
  disabled,
  onChange,
  onCustomerUpserted,
  customerErrorShown,
  onReduceGuestCount,
  performGuestCheckinCheckout,
  cityTaxExonerationFromReservation,
}: GuestFormProps) {
  const { id: tenantId } = useTenant()
  const { t } = useTranslation()
  const formatDate = useFormatDate()
  const showConfirm = useConfirm()
  const getErrorMessages = useGetErrorMessage()
  const { currentPhase, nextPhaseTransitionsAllowed } = useFrontendPhase(value.start, value.end, value.phase)
  const hotelDate = useHotelDate()

  const guestContext = useMemo(() => rrContext.guests[index], [rrContext.guests, index])

  const { data, refetch, error } = useCustomerDocumentImagesQuery({
    variables: {
      filter: {
        customerId: value.guestId ?? '',
        tenantId: tenantId,
      },
    },
    skip: true,
  })
  const [idDocsUrl, setIdDocsUrl] = useState<string[] | undefined>()

  useEffect(() => {
    if (value.guestId) {
      refetch({
        filter: {
          customerId: value.guestId,
          tenantId: tenantId,
        },
      }).then((d) => {
        if (d.data && !d.error) {
          const idDocs = d.data.customerDocumentImages?.filter((doc) => doc.type === 'IDENTIFICATION_DOCUMENT') ?? []
          if (idDocs.length > 0) {
            setIdDocsUrl(idDocs.map((idDoc) => idDoc.file.URL))
          }
        }
      })
    }
  }, [data, error, value.guestId, tenantId])

  const guestError = useMemo(() => {
    const show = formErrors?.guestId ? true : formErrors?.language ? true : formErrors?.lastName ? true : formErrors?.firstName ? true : false
    const message = formErrors?.guestId?.message
      ? formErrors?.guestId?.message
      : formErrors?.language?.message
      ? formErrors?.language?.message
      : formErrors?.lastName?.message
      ? formErrors?.lastName?.message
      : formErrors?.firstName?.message
      ? formErrors?.firstName?.message
      : ''
    return { show, message }
  }, [formErrors])

  const customerError = useMemo(() => {
    const show = guestContext?.customerError !== null && guestContext?.customerError !== undefined
    const message = getErrorMessages(guestContext?.customerError).join(', ')
    return { show, message }
  }, [guestContext?.customerError])

  const checkInWarningMessage = useMemo(() => {
    return getGuestCheckInWarningMessage(t, checkInWarning, rrContext.hasChanged)
  }, [t, rrContext.hasChanged, checkInWarning])

  const onReduceGuestCountClick = useCallback(() => {
    const rr = context.reservation.reservedResources ? context.reservation.reservedResources.find((rr) => rr.id === rrContext.reservedResourceId) : undefined
    if (rr) {
      const operationType = guestReductionOperationType(rr)

      if (['ADULTS_REDUCTION', 'CHILDREN_REDUCTION'].includes(operationType)) {
        showConfirm({
          title: t('pms.booking.reservation.guest.action.reduce_guest_count_title'),
          message: t('pms.booking.reservation.guest.action.reduce_guest_count_message'),
          confirmButtonText: t('commons.ok'),
          cancelButtonText: t('commons.cancel'),
          onConfirm: onReduceGuestCount,
        })
      } else {
        onReduceGuestCount()
      }
    }
  }, [onReduceGuestCount, t, context])

  const onChangeBirthDate = useCallback(
    (d: any, context: PickerChangeHandlerContext<DateValidationError>) => {
      if (isValidDate(d)) {
        onChange({ ...value, birthDate: d })
      } else if (!context) {
        onChange({ ...value, birthDate: null })
      }
    },
    [value, onChange],
  )

  const birthAddressError = useMemo(() => {
    const e = formErrors?.birthAddress as any
    const show = e ? true : false

    const message = e?.message //
      ? e?.message
      : e?.country?.message
      ? e?.country?.message
      : e?.country?.id?.message
      ? e?.country?.id?.message
      : e?.country?.isoCode?.message
      ? e?.country?.isoCode?.message
      : e?.municipalityId?.message
      ? e?.municipalityId?.message
      : e?.province?.message
      ? e?.province?.message
      : ''

    return { show, message }
  }, [formErrors])

  const birthAddress = useMemo(() => (value.birthAddress ? { countryId: value.birthAddress.countryId, municipalityId: value.birthAddress.municipalityId } : null), [value.birthAddress])

  const onBirthAddressChange = useCallback(
    (a: CountryMunicipality | null) => {
      const birthAddr: GuestAddress | null = a
        ? {
            countryId: a.country.id,
            municipalityId: a.municipality?.id ?? null,
            province: a.municipality?.province ?? null,
            city: a.country.isoCode === 'IT' ? a.municipality?.name ?? '' : null,
          }
        : null

      const addr = value.address ?? birthAddr
      const nationalityId = value.nationalityId ?? birthAddr?.countryId ?? null

      onChange({
        ...value,
        birthAddress: birthAddr,
        address: addr,
        nationalityId,
      })
    },
    [onChange, value],
  )

  const addressError = useMemo(() => {
    const e = formErrors?.address as any
    const show = e ? true : false

    const message = e?.message //
      ? e?.message
      : e?.country?.message
      ? e?.country?.message
      : e?.country?.id?.message
      ? e?.country?.id?.message
      : e?.country?.isoCode?.message
      ? e?.country?.isoCode?.message
      : e?.municipalityId?.message
      ? e?.municipalityId?.message
      : e?.province?.message
      ? e?.province?.message
      : ''

    return { show, message }
  }, [formErrors])

  const address = useMemo(() => (value.address ? { countryId: value.address.countryId, municipalityId: value.address.municipalityId } : null), [value.address])

  const onAddressChange = useCallback(
    (a: CountryMunicipality | null) => {
      const aa: GuestAddress | null = a
        ? {
            countryId: a.country.id,
            municipalityId: a.municipality?.id ?? null,
            province: a.municipality?.province ?? null,
            city: a.country.isoCode === 'IT' ? a.municipality?.name ?? '' : null,
          }
        : null

      onChange({
        ...value,
        address: aa,
      })
    },
    [onChange, value],
  )

  const forceShowDates = useMemo(() => {
    if (value.start && compareDates(value.start, rrContext.start) !== 0) return true
    if (value.end && compareDates(value.end, rrContext.end) !== 0) return true
    return formErrors?.start ?? formErrors?.end ? true : false
  }, [value.start, value.end, rrContext.start, rrContext.end, formErrors?.start, formErrors?.end])

  const [showDates, setShowDates] = useState(false)

  useEffect(() => {
    if (forceShowDates) {
      setShowDates(true)
    }
  }, [forceShowDates])

  const toggleShowDates = useCallback(() => setShowDates(!showDates), [showDates])

  const [showTaxExoneration, setShowTaxExoneration] = useState(false)

  const toggleShowTaxExoneration = useCallback(() => setShowTaxExoneration(!showTaxExoneration), [showTaxExoneration])

  const forceShowTaxExoneration = useMemo(() => {
    if (value.cityTaxExoneration || cityTaxExonerationFromReservation) return true
    return false
  }, [value.cityTaxExoneration, cityTaxExonerationFromReservation])

  useEffect(() => {
    if (cityTaxExonerationFromReservation && !value.cityTaxExoneration) {
      onChange({ ...value, cityTaxExoneration: cityTaxExonerationFromReservation })
    }
  }, [cityTaxExonerationFromReservation, value.cityTaxExoneration])

  const onChangeStart = useCallback(
    (d: any, context: PickerChangeHandlerContext<DateValidationError>) => {
      if (isValidDate(d)) {
        onChange({ ...value, start: d })
      } else if (!context.validationError?.length) {
        onChange({ ...value, start: null })
      }
    },
    [value, onChange],
  )

  const onChangeEnd = useCallback(
    (d: any, context: PickerChangeHandlerContext<DateValidationError>) => {
      if (isValidDate(d)) {
        onChange({ ...value, end: d })
      } else if (!context.validationError?.length) {
        onChange({ ...value, end: null })
      }
    },
    [value, onChange],
  )

  const showIdentificationDocument = useMemo(() => {
    return isGuarantor(value.type) || value.identificationDocument || formErrors?.identificationDocument
  }, [value, formErrors])

  const inHouseOption: MenuActionType | undefined = useMemo(() => {
    const allowedHome = nextPhaseTransitionsAllowed.find((t) => t.frontendPhase === FrontendPhase.Home)
    if (allowedHome)
      return {
        action: () => {
          if (value.guestId) performGuestCheckinCheckout(value.guestId, allowedHome.action)
        },
        disabled: !value.guestId || checkInWarning?.missingRequiredData || checkInWarning?.wrongGuestType || rrContext.hasChanged || compareDates(rrContext.start, hotelDate) > 0,
      }
  }, [nextPhaseTransitionsAllowed, value, checkInWarning, rrContext.hasChanged, rrContext.start, hotelDate])

  const notYetArrivedOption: MenuActionType | undefined = useMemo(() => {
    const allowedNotYetArrived = nextPhaseTransitionsAllowed.find((t) => t.frontendPhase === FrontendPhase.NotYetArrived)
    if (allowedNotYetArrived)
      return {
        action: () => {
          if (value.guestId) performGuestCheckinCheckout(value.guestId, allowedNotYetArrived.action)
        },
        disabled: !value ?? true,
      }
  }, [nextPhaseTransitionsAllowed, value])

  const departedOption: MenuActionType | undefined = useMemo(() => {
    const allowedDeparted = nextPhaseTransitionsAllowed.find((t) => t.frontendPhase === FrontendPhase.Departed)
    if (allowedDeparted)
      return {
        action: () => {
          if (value.guestId) performGuestCheckinCheckout(value.guestId, allowedDeparted.action)
        },
      }
  }, [nextPhaseTransitionsAllowed, value])

  const tooltipPhaseCell =
    value.checkin || value.checkout
      ? value.checkin && value.checkout
        ? value.checkin > value.checkout
          ? t('pms.booking.reservation.guest.phase.label.checkin', { date: formatDate(value.checkin), hour: formatDate(value.checkin, 'pp') })
          : t('pms.booking.reservation.guest.phase.label.checkout', { date: formatDate(value.checkout), hour: formatDate(value.checkout, 'pp') })
        : value.checkin
        ? t('pms.booking.reservation.guest.phase.label.checkin', { date: formatDate(value.checkin), hour: formatDate(value.checkin, 'pp') })
        : value.checkout
        ? t('pms.booking.reservation.guest.phase.label.checkout', { date: formatDate(value.checkout), hour: formatDate(value.checkout, 'pp') })
        : null
      : null

  return (
    <Grid item xs={12} container spacing={1} alignItems="center">
      <Grid item xs="auto" sx={(theme: Theme) => ({ color: customerError.show ? theme.palette.error.main : checkInWarningMessage ? theme.palette.warning.main : undefined })}>
        <strong>{t('pms.booking.reservation.guest.label', { index: index + 1 })}</strong>
      </Grid>
      <Grid item xs="auto">
        <Tooltip title={tooltipPhaseCell}>
          <span>
            <ReservedResourcePhaseCell phase={currentPhase} />
          </span>
        </Tooltip>
      </Grid>

      <Grid item xs>
        <Divider light />
      </Grid>
      <Grid item xs="auto">
        <EditButton //
          disabled={disabled ?? false}
          value={value}
          onCustomerUpserted={onCustomerUpserted}
          forceShowDates={forceShowDates}
          showDates={showDates}
          onToggleShowDates={toggleShowDates}
          showTaxExoneration={showTaxExoneration}
          onToggleShowTaxExoneration={toggleShowTaxExoneration}
          forceShowTaxExoneration={forceShowTaxExoneration}
          inHouse={inHouseOption}
          notYetArrived={notYetArrivedOption}
          departed={departedOption}
          reservedResourceId={rrContext.reservedResourceId}
        />

        <IconButton //
          size="small"
          onClick={onReduceGuestCountClick}
          disabled={disabled || value.phase === ReservedResourcePhase.CHECKIN || value.phase === ReservedResourcePhase.CHECKOUT}
        >
          <FontAwesomeIcon icon={faTrash} fontSize={'1rem'} />
        </IconButton>
      </Grid>
      {checkInWarningMessage !== null && (
        <Grid item xs={12}>
          <Alert severity="warning" variant="outlined">
            {checkInWarningMessage}
          </Alert>
        </Grid>
      )}

      {customerError.show && (
        <Grid item xs={12}>
          <Alert severity="error" variant="outlined" onClose={customerErrorShown}>
            {customerError.message}
          </Alert>
        </Grid>
      )}

      <Grid container spacing={1} columns={24} p={1}>
        <Grid item xs={12}>
          <Grid container columns={12} spacing={1}>
            <Grid item xs="auto">
              <FormControl sx={{ width: 60 }} error={formErrors?.type ? true : false}>
                <HicFormLabel error={formErrors?.type ? true : false}>{t('pms.booking.reservation.guest.guest_type')}</HicFormLabel>
                <GuestTypeSelect //
                  size="small"
                  value={value.type}
                  onChange={(type) => onChange({ ...value, type })}
                  disabled={disabled || value.phase === ReservedResourcePhase.CHECKOUT}
                  group={rrContext.group}
                  groupMemberConsiderAsSingleGuest={isGroupMemberToConsiderAsSingleGuest(rrContext.group, value, context.reservation)}
                />
                {(formErrors?.type?.message ? true : false) && <FormHelperText error>{formErrors?.type?.message}</FormHelperText>}
              </FormControl>
            </Grid>
            <Grid item xs>
              <FormControl fullWidth error={guestError.show}>
                <HicFormLabel error={guestError.show}>
                  <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ heigth: '23px' }}>
                    <>{t('pms.booking.reservation.guest.lastName_firstName')}</>
                    {/* <LastNameFirstNamePopover //
                  lastName={value.lastName || null}
                  onLastNameChange={(lastName) =>
                    onChange({
                      //
                      ...value,
                      guestId: value.guestId === null ? '' : value.guestId,
                      lastName,
                    })
                  }
                  lastNameError={formErrors?.lastName}
                  firstName={value.firstName || null}
                  onFirstNameChange={(firstName) =>
                    onChange({
                      //
                      ...value,
                      guestId: value.guestId === null ? '' : value.guestId,
                      firstName,
                    })
                  }
                  firstNameError={formErrors?.firstName}
                  language={value.language || null}
                  onLanguageChange={(language) =>
                    onChange({
                      //
                      ...value,
                      guestId: value.guestId === null ? '' : value.guestId,
                      language,
                    })
                  }
                  languageError={formErrors?.language}
                  disabled={disabled}
                /> */}
                  </Stack>
                </HicFormLabel>
                <OccupancyGuestLookup //
                  context={context}
                  value={value}
                  onChange={onChange}
                  error={guestError.show}
                  disabled={disabled || value.phase === ReservedResourcePhase.CHECKIN || value.phase === ReservedResourcePhase.CHECKOUT}
                  size="small"
                />
                {guestError.show && <FormHelperText error>{guestError.message}</FormHelperText>}
              </FormControl>
            </Grid>
            <Grid item xs={'auto'}>
              <FormControl sx={{ width: 60 }} error={formErrors?.gender ? true : false}>
                <HicFormLabel error={formErrors?.gender ? true : false}>{t('pms.booking.reservation.guest.gender')}</HicFormLabel>
                <GenderSelect //
                  value={value.gender}
                  size="small"
                  onChange={(gender) => onChange({ ...value, gender })}
                  disabled={disabled}
                  error={formErrors?.gender ? true : false}
                />
                {(formErrors?.gender?.message ? true : false) && <FormHelperText error>{formErrors?.gender?.message}</FormHelperText>}
              </FormControl>
            </Grid>
            <Grid item xs={'auto'}>
              <FormControl sx={{ width: 100 }} error={formErrors?.birthDate ? true : false}>
                <HicFormLabel error={formErrors?.birthDate ? true : false}>{t('pms.booking.reservation.guest.birth_date')}</HicFormLabel>
                <DatePicker //
                  value={value.birthDate}
                  onChange={onChangeBirthDate}
                  disabled={disabled}
                  slotProps={{ textField: { size: 'small', error: formErrors?.birthDate ? true : false, InputProps: { endAdornment: undefined } } }}
                />
                {(formErrors?.birthDate?.message ? true : false) && <FormHelperText error>{formErrors?.birthDate?.message}</FormHelperText>}
              </FormControl>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <Grid container columns={12} spacing={1}>
            <Grid item xs={4}>
              <FormControl fullWidth error={birthAddressError.show}>
                <HicFormLabel error={birthAddressError.show}>{t('pms.booking.reservation.guest.birth_address')}</HicFormLabel>
                <CountryMunicipalityLookup //
                  size="small"
                  value={birthAddress}
                  onChange={onBirthAddressChange}
                  disabled={disabled}
                  error={birthAddressError.show}
                />
                {birthAddressError.show && <FormHelperText error>{birthAddressError.message}</FormHelperText>}
              </FormControl>
            </Grid>
            <Grid item xs={4}>
              <FormControl fullWidth error={addressError.show}>
                <HicFormLabel error={addressError.show}>{t('pms.booking.reservation.guest.address')}</HicFormLabel>
                <CountryMunicipalityLookup //
                  size="small"
                  value={address}
                  onChange={onAddressChange}
                  disabled={disabled}
                  error={addressError.show}
                />
                {addressError.show && <FormHelperText error>{addressError.message}</FormHelperText>}
              </FormControl>
            </Grid>
            <Grid item xs={4}>
              <FormControl fullWidth error={formErrors?.nationalityId ? true : false}>
                <HicFormLabel error={formErrors?.nationalityId ? true : false}>{t('pms.booking.reservation.guest.nationality')}</HicFormLabel>
                <CountrySelect //
                  size="small"
                  value={value.nationalityId}
                  onChange={(cId) => onChange({ ...value, nationalityId: cId })}
                  disabled={disabled}
                  error={formErrors?.nationalityId ? true : false}
                />
                {(formErrors?.nationalityId ? true : false) && <FormHelperText error>{formErrors?.nationalityId?.message}</FormHelperText>}
              </FormControl>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={24} container spacing={1} columns={24}>
          {(showTaxExoneration || forceShowTaxExoneration) && (
            <>
              <Grid item xs={4}>
                <FormControl fullWidth error={formErrors?.cityTaxExoneration ? true : false}>
                  <HicFormLabel error={formErrors?.cityTaxExoneration ? true : false}>{t('pms.booking.reservation.guest.city_tax_exoneration')}</HicFormLabel>
                  <CityTaxExonerationLookup //
                    size="small"
                    propertyId={propertyId}
                    value={value.cityTaxExoneration ? { code: value.cityTaxExoneration } : cityTaxExonerationFromReservation ? { code: cityTaxExonerationFromReservation } : null}
                    onChange={(exoneration) => onChange({ ...value, cityTaxExoneration: exoneration?.code ?? null })}
                    disabled={disabled}
                    error={formErrors?.cityTaxExoneration ? true : false}
                  />
                  {(formErrors?.cityTaxExoneration ? true : false) && <FormHelperText error>{formErrors?.cityTaxExoneration?.message}</FormHelperText>}
                </FormControl>
              </Grid>
            </>
          )}

          {(showDates || forceShowDates) && (
            <>
              <Grid item xs={4}>
                <FormControl fullWidth error={formErrors?.start ? true : false}>
                  <HicFormLabel error={formErrors?.start ? true : false}>{t('pms.booking.reservation.guest.start')}</HicFormLabel>
                  <DatePicker //
                    value={value.start}
                    onChange={onChangeStart}
                    disabled={disabled || value.phase === ReservedResourcePhase.CHECKIN || value.phase === ReservedResourcePhase.CHECKOUT}
                    slotProps={{
                      textField: {
                        size: 'small',
                        error: formErrors?.start ? true : false,
                      },
                    }}
                  />
                  {(formErrors?.start?.message ? true : false) && <FormHelperText error>{formErrors?.start?.message}</FormHelperText>}
                </FormControl>
              </Grid>
              <Grid item xs={4}>
                <FormControl fullWidth error={formErrors?.end ? true : false}>
                  <HicFormLabel error={formErrors?.end ? true : false}>{t('pms.booking.reservation.guest.end')}</HicFormLabel>
                  <DatePicker //
                    value={value.end}
                    onChange={onChangeEnd}
                    disabled={disabled || value.phase === ReservedResourcePhase.CHECKOUT}
                    slotProps={{
                      textField: {
                        size: 'small',
                        InputProps: {
                          endAdornment: undefined,
                        },
                      },
                    }}
                  />
                  {(formErrors?.end?.message ? true : false) && <FormHelperText error>{formErrors?.end?.message}</FormHelperText>}
                </FormControl>
              </Grid>
            </>
          )}
        </Grid>
        {showIdentificationDocument && (
          <IdentificationDocument //
            value={value.identificationDocument}
            comingFromWci={guestContext?.comingFromWci ?? false}
            onChange={(identificationDocument) => onChange({ ...value, identificationDocument })}
            disabled={disabled || value.phase === ReservedResourcePhase.CHECKOUT}
            error={formErrors?.identificationDocument}
            docsUrls={idDocsUrl}
          />
        )}
      </Grid>
    </Grid>
  )
}
